/**
 * AssetUI — shared design primitives for the asset management module.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, AlertCircle, Download, Filter, Search, X, RefreshCw, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { AssetStatus, AssetCondition, DisposalStatus, MaintenanceStatus } from "../types";

// ─── Buttons ──────────────────────────────────────────────────────────────────

const BASE = "inline-flex items-center gap-2 rounded-xl text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]";

const VARIANTS = {
  primary:       "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 focus-visible:ring-indigo-500",
  secondary:     "bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 focus-visible:ring-slate-400",
  ghost:         "text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-400",
  danger:        "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500",
  dangerOutline: "border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 focus-visible:ring-red-400",
  emerald:       "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-500",
  amber:         "bg-amber-500 text-white shadow-sm hover:bg-amber-600 focus-visible:ring-amber-400",
} as const;

const SIZES = { sm: "px-3 py-1.5", md: "px-4 py-2", lg: "px-5 py-2.5", icon: "p-2" } as const;

export type BtnVariant = keyof typeof VARIANTS;
export type BtnSize    = keyof typeof SIZES;

export function btnCls(variant: BtnVariant = "secondary", size: BtnSize = "md", extra?: string) {
  return [BASE, VARIANTS[variant], SIZES[size], extra].filter(Boolean).join(" ");
}

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: LucideIcon;
  loading?: boolean;
  loadingText?: string;
};

export function Btn({ variant = "secondary", size = "md", icon: Icon, loading, loadingText, children, className, disabled, ...rest }: BtnProps) {
  return (
    <button {...rest} disabled={disabled || loading} className={btnCls(variant, size, className)}>
      {loading ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
      {loading ? (loadingText ?? "Loading…") : children}
    </button>
  );
}

export function LinkBtn({ to, variant = "secondary", size = "md", icon: Icon, children, className }: { to: string; variant?: BtnVariant; size?: BtnSize; icon?: LucideIcon; children: React.ReactNode; className?: string }) {
  return (
    <Link to={to} className={btnCls(variant, size, className)}>
      {Icon && <Icon className="h-4 w-4 shrink-0" />}{children}
    </Link>
  );
}

export function BackBtn({ to, label = "Back" }: { to?: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => (to ? navigate(to) : navigate(-1))} className={btnCls("secondary", "md")}>
      <ArrowLeft className="h-4 w-4" />{label}
    </button>
  );
}

// ─── Status badges ────────────────────────────────────────────────────────────

const ASSET_STATUS: Record<AssetStatus, { dot: string; text: string; bg: string; border: string; label: string }> = {
  AVAILABLE:         { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", label: "Available"         },
  ASSIGNED:          { dot: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50",     border: "border-blue-200",    label: "Assigned"          },
  UNDER_MAINTENANCE: { dot: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   label: "Under Maintenance" },
  PENDING_DISPOSAL:  { dot: "bg-purple-400",  text: "text-purple-700",  bg: "bg-purple-50",   border: "border-purple-200",  label: "Pending Disposal"  },
  DISPOSED:          { dot: "bg-slate-400",   text: "text-slate-500",   bg: "bg-slate-100",   border: "border-slate-200",   label: "Disposed"          },
  LOST:              { dot: "bg-red-500",     text: "text-red-700",     bg: "bg-red-50",      border: "border-red-200",     label: "Lost"              },
  DAMAGED:           { dot: "bg-orange-500",  text: "text-orange-700",  bg: "bg-orange-50",   border: "border-orange-200",  label: "Damaged"           },
};

const CONDITION: Record<AssetCondition, { text: string; bg: string; label: string }> = {
  EXCELLENT: { text: "text-emerald-700", bg: "bg-emerald-100", label: "Excellent" },
  GOOD:      { text: "text-blue-700",    bg: "bg-blue-100",    label: "Good"      },
  FAIR:      { text: "text-amber-700",   bg: "bg-amber-100",   label: "Fair"      },
  POOR:      { text: "text-orange-700",  bg: "bg-orange-100",  label: "Poor"      },
  DAMAGED:   { text: "text-red-700",     bg: "bg-red-100",     label: "Damaged"   },
};

const DISPOSAL_STATUS: Record<DisposalStatus, { dot: string; text: string; bg: string; border: string; label: string }> = {
  PENDING:   { dot: "bg-amber-400", text: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200",  label: "Pending Review" },
  APPROVED:  { dot: "bg-blue-500",  text: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-200",   label: "Approved"       },
  COMPLETED: { dot: "bg-slate-400", text: "text-slate-500",  bg: "bg-slate-100",  border: "border-slate-200",  label: "Completed"      },
  REJECTED:  { dot: "bg-red-500",   text: "text-red-700",    bg: "bg-red-50",     border: "border-red-200",    label: "Rejected"       },
  CANCELLED: { dot: "bg-slate-400", text: "text-slate-500",  bg: "bg-slate-100",  border: "border-slate-200",  label: "Cancelled"      },
};

const MAINTENANCE_STATUS: Record<MaintenanceStatus, { dot: string; text: string; bg: string; border: string; label: string }> = {
  PENDING:     { dot: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   label: "Pending"     },
  IN_PROGRESS: { dot: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50",    border: "border-blue-200",    label: "In Progress" },
  COMPLETED:   { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Completed"   },
  CANCELLED:   { dot: "bg-slate-400",   text: "text-slate-500",   bg: "bg-slate-100",  border: "border-slate-200",   label: "Cancelled"   },
  DEFERRED:    { dot: "bg-purple-400",  text: "text-purple-700",  bg: "bg-purple-50",  border: "border-purple-200",  label: "Deferred"    },
};

export function AssetStatusBadge({ value }: { value?: AssetStatus | null }) {
  const cfg = value ? ASSET_STATUS[value] : null;
  if (!cfg) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
  );
}

export function ConditionBadge({ value }: { value?: AssetCondition | null }) {
  const cfg = value ? CONDITION[value] : null;
  if (!cfg) return <span className="text-xs text-slate-400">—</span>;
  return <span className={`inline-flex rounded-lg px-2 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
}

export function MaintenanceBadge({ value }: { value?: MaintenanceStatus | null }) {
  const cfg = value ? MAINTENANCE_STATUS[value] : null;
  if (!cfg) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
  );
}

export function DisposalBadge({ value }: { value?: DisposalStatus | null }) {
  const cfg = value ? DISPOSAL_STATUS[value] : null;
  if (!cfg) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
  );
}

// ─── Layout primitives ────────────────────────────────────────────────────────

export function PageHeader({ breadcrumb, title, description, actions }: { breadcrumb?: string; title: string; description?: string; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-5">
      <div className="min-w-0">
        {breadcrumb && <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-slate-400">{breadcrumb}</p>}
        <h1 className="text-xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className, noPad }: { children: React.ReactNode; className?: string; noPad?: boolean }) {
  return <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${noPad ? "" : "p-5"} ${className ?? ""}`}>{children}</div>;
}

export function CardHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

export function Table({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${className ?? ""}`}><div className="overflow-x-auto"><table className="min-w-full text-left text-sm">{children}</table></div></div>;
}
export function THead({ children }: { children: React.ReactNode }) {
  return <thead className="border-b bg-slate-50"><tr>{children}</tr></thead>;
}
export function TH({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 ${right ? "text-right" : ""}`}>{children}</th>;
}
export function TBody({ children }: { children: React.ReactNode }) {
  return <tbody className="divide-y divide-slate-100">{children}</tbody>;
}
export function TR({ children, className }: { children: React.ReactNode; className?: string }) {
  return <tr className={`transition-colors hover:bg-slate-50/60 ${className ?? ""}`}>{children}</tr>;
}
export function TD({ children, className, right, muted }: { children?: React.ReactNode; className?: string; right?: boolean; muted?: boolean }) {
  return <td className={`px-4 py-3.5 ${right ? "text-right" : ""} ${muted ? "text-slate-400" : ""} ${className ?? ""}`}>{children}</td>;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function Sk({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-slate-200 ${className ?? ""}`} />;
}

export function AssetLoading({ label }: { label?: string }) {
  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="border-b bg-slate-50 px-5 py-4"><Sk className="h-3.5 w-40" /></div>
        {[1,2,3,4,5].map((i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <Sk className="h-3 w-24" /><Sk className="h-3 w-36" /><Sk className="h-3 w-20" /><Sk className="ml-auto h-5 w-16 rounded-full" />
          </div>
        ))}
      </div>
      {label && <p className="text-center text-xs text-slate-400">{label}</p>}
    </div>
  );
}

// ─── Error ────────────────────────────────────────────────────────────────────

export function AssetError({ message, onRetry }: { message?: string | null; onRetry?: () => void }) {
  const text = message || "An unexpected error occurred.";
  // A 403 from the backend surfaces here as plain text (e.g. "You do not have permission to
  // perform this action") — show a calmer, specific card instead of a scary generic error,
  // and skip the Retry button since retrying a permission denial never helps.
  const isAccessDenied = /permission|forbidden|access is denied|not authorized/i.test(text);
  if (isAccessDenied) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="flex-1">
          <p className="font-semibold text-amber-800">You don&apos;t have access to this</p>
          <p className="mt-0.5 text-sm text-amber-700">Ask an administrator if you need this permission.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
      <div className="flex-1">
        <p className="font-semibold text-red-800">Something went wrong</p>
        <p className="mt-0.5 text-sm text-red-600">{text}</p>
      </div>
      {onRetry && <button type="button" onClick={onRetry} className="shrink-0 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 shadow-sm transition hover:bg-red-50">Retry</button>}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function EmptyState({ icon: Icon, title, description, action }: { icon: LucideIcon; title: string; description?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100"><Icon className="h-8 w-8 text-slate-400" /></div>
      <h3 className="mt-4 font-semibold text-slate-700">{title}</h3>
      {description && <p className="mt-1 max-w-xs text-sm text-slate-400">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import type { PageResponse } from "../types";

function buildPageNums(cur: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const s = new Set([0, 1, cur - 1, cur, cur + 1, total - 2, total - 1]);
  const arr = [...s].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  let prev = -1;
  for (const p of arr) { if (prev !== -1 && p - prev > 1) out.push("…"); out.push(p); prev = p; }
  return out;
}

export function Pagination<T>({ page, onPageChange, onSizeChange, sizes = [10, 20, 50] }: { page: PageResponse<T>; onPageChange: (p: number) => void; onSizeChange?: (s: number) => void; sizes?: number[] }) {
  const cur = page.page, total = Math.max(1, page.totalPages ?? 1);
  const isFirst = cur <= 0, isLast = cur >= total - 1;
  const nb = "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-400"><span className="font-semibold text-slate-700">{page.totalElements ?? 0}</span> result{page.totalElements !== 1 ? "s" : ""}</span>
        {onSizeChange && <select className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold outline-none focus:border-indigo-500" value={page.size} onChange={(e) => onSizeChange(Number(e.target.value))}>{sizes.map((s) => <option key={s} value={s}>{s} / page</option>)}</select>}
      </div>
      <div className="flex items-center gap-0.5">
        <button type="button" disabled={isFirst} onClick={() => onPageChange(0)} className={nb}><ChevronsLeft className="h-4 w-4" /></button>
        <button type="button" disabled={isFirst} onClick={() => onPageChange(cur - 1)} className={nb}><ChevronLeft className="h-4 w-4" /></button>
        {buildPageNums(cur, total).map((p, i) => p === "…" ? <span key={`e${i}`} className="flex h-8 w-8 items-center justify-center text-xs text-slate-300">…</span> : <button key={p} type="button" onClick={() => onPageChange(p)} className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-semibold transition-colors ${p === cur ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>{p + 1}</button>)}
        <button type="button" disabled={isLast} onClick={() => onPageChange(cur + 1)} className={nb}><ChevronRight className="h-4 w-4" /></button>
        <button type="button" disabled={isLast} onClick={() => onPageChange(total - 1)} className={nb}><ChevronsRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

// ─── Currency formatter ───────────────────────────────────────────────────────

export function fmt(v?: number | null, currency = "KES") {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-KE", { style: "currency", currency, maximumFractionDigits: 0 }).format(v);
}

export function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

export function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return v; }
}

// ─── Search input ─────────────────────────────────────────────────────────────

export function SearchInput({
  value, onChange, placeholder = "Search…", className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className ?? ""}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      />
      {value && (
        <button type="button" onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-slate-400 hover:text-slate-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── Select filter ────────────────────────────────────────────────────────────

export function SelectFilter({
  label, value, onChange, options, className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${className ?? ""}`}
    >
      <option value="">{label}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ─── Date range pair ──────────────────────────────────────────────────────────

export function DateRangeFilter({
  labelFrom = "From", labelTo = "To",
  from, to, onFromChange, onToChange,
}: {
  labelFrom?: string; labelTo?: string;
  from: string; to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-slate-400">{labelFrom}</span>
        <input type="date" value={from} onChange={(e) => onFromChange(e.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-slate-400">{labelTo}</span>
        <input type="date" value={to} onChange={(e) => onToChange(e.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
      </div>
    </>
  );
}

// ─── Filter panel wrapper ─────────────────────────────────────────────────────

export function FilterPanel({
  activeCount = 0,
  onClear,
  onRefresh,
  children,
}: {
  activeCount?: number;
  onClear?: () => void;
  onRefresh?: () => void;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold transition-all ${open || activeCount > 0 ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Filter className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">{activeCount}</span>
          )}
        </button>
        {activeCount > 0 && onClear && (
          <button type="button" onClick={onClear}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition">
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
        {onRefresh && (
          <button type="button" onClick={onRefresh}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Active filter badges ─────────────────────────────────────────────────────

export function ActiveFilterBadges({
  filters,
}: {
  filters: { label: string; onRemove: () => void }[];
}) {
  if (!filters.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {filters.map((f) => (
        <span key={f.label}
          className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
          {f.label}
          <button type="button" onClick={f.onRemove} className="ml-0.5 rounded-full text-indigo-400 hover:text-indigo-700">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

// ─── Export button ────────────────────────────────────────────────────────────

export function ExportBtn({
  onExport,
  loading = false,
  label = "Export XLS",
}: {
  onExport: () => void | Promise<void>;
  loading?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      disabled={loading}
      onClick={onExport}
      className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:pointer-events-none disabled:opacity-50"
    >
      {loading
        ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        : <Download className="h-4 w-4" />
      }
      {loading ? "Exporting…" : label}
    </button>
  );
}
